import { Job } from '../types';

// Cache for jobs data (envelope: { date, jobs } or legacy top-level array)
let jobsCache: Job[] | null = null;
/** ISO timestamp from jobs.json when present (scraper run time). */
let jobsFileDateCache: string | null = null;

// Fetch jobs data from jobs.json
const fetchJobsFromJson = async (): Promise<[Job[], string | null]> => {
  if (jobsCache && jobsFileDateCache) {
    return [jobsCache, jobsFileDateCache];
  }
  else if (jobsCache) {
    return [jobsCache, null];
  }

  try {
    const response = await fetch('/jobs.json');
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data: unknown = await response.json();

    if (Array.isArray(data)) {
      jobsCache = data as Job[];
      jobsFileDateCache = null;
    } else if (
      data &&
      typeof data === 'object' &&
      'jobs' in data &&
      Array.isArray((data as { jobs: unknown }).jobs)
    ) {
      const envelope = data as { date?: unknown; jobs: Job[] };
      jobsCache = envelope.jobs;
      jobsFileDateCache =
        typeof envelope.date === 'string' ? envelope.date : null;
    } else {
      console.warn('Invalid jobs data format');
      jobsCache = [];
      jobsFileDateCache = null;
    }

    console.log(
      'Jobs data fetched successfully from jobs.json:',
      jobsCache.length,
      'jobs'
    );
    return [jobsCache, jobsFileDateCache];
  } catch (error) {
    console.warn('Could not fetch jobs.json:', error);
    // Return empty data structure
    return [[], null];
  }
};

// Transform jobs to match our Job interface (if needed)
export const loadJobsFromJson = async (): Promise<Job[]> => {
  await fetchJobsFromJson();
  return jobsCache ?? [];
};

/** Scraper run timestamp from jobs.json envelope, if available. */
export const getJobsFileDateFromJson = async (): Promise<string | null> => {
  await fetchJobsFromJson();
  return jobsFileDateCache;
};

// Get job statistics
export const getJobStatsFromJson = async () => {
  const jobs = await loadJobsFromJson();
  const totalJobs = jobs.length;
  
  // Count by company
  const companyCounts: { [key: string]: number } = {};
  jobs.forEach(job => {
    companyCounts[job.company] = (companyCounts[job.company] || 0) + 1;
  });

  // Count by type
  const typeCounts: { [key: string]: number } = {};
  jobs.forEach(job => {
    typeCounts[job.type] = (typeCounts[job.type] || 0) + 1;
  });

  // Count remote vs on-site
  const remoteCount = jobs.filter(job => job.remote).length;
  const onSiteCount = totalJobs - remoteCount;

  // Get unique tags
  const allTags = new Set<string>();
  jobs.forEach(job => {
    job.tags.forEach(tag => allTags.add(tag));
  });

  const scrapeDate = await getJobsFileDateFromJson();

  const stats = {
    totalJobs,
    companyCounts,
    typeCounts,
    remoteCount,
    onSiteCount,
    uniqueTags: Array.from(allTags),
    lastUpdated: scrapeDate ?? new Date().toISOString(),
  };

  console.log('Job stats calculated from jobs.json:', stats);
  return stats;
};

// Get unique companies for filtering
export const getUniqueCompaniesFromJson = async (): Promise<string[]> => {
  const jobs = await loadJobsFromJson();
  const companies = new Set<string>();
  jobs.forEach(job => companies.add(job.company));
  return Array.from(companies).sort();
};

// Get unique locations for filtering
export const getUniqueLocationsFromJson = async (): Promise<string[]> => {
  const jobs = await loadJobsFromJson();
  const locations = new Set<string>();
  
  jobs.forEach(job => {
    if (job.location && job.location.length > 0) {
      for (const loc of job.location) {
        // Skip blank or empty locations
        if (!loc || loc.trim() === '' || loc === 'N/A') {
          continue;
        }
        
        // Extract country from location string
        const parts = loc.split(', ');
        const country = parts[parts.length - 1]; // Last part is country
        
        // Skip if country is blank or N/A
        if (!country || country.trim() === '' || country === 'N/A') {
          continue;
        }
        
        if (job.remote) {
          // For remote jobs, group by country
          locations.add(`Remote`);
        } else {
          // For non-remote jobs, add the full location
          locations.add(loc);
        }
      }
    }
  });
  
  return ['All Locations', ...Array.from(locations).sort()];
};

// Get all unique tags for filtering
export const getAllUniqueTagsFromJson = async (): Promise<string[]> => {
  const jobs = await loadJobsFromJson();
  const tags = new Set<string>();
  jobs.forEach(job => {
    job.tags.forEach(tag => tags.add(tag));
  });
  return Array.from(tags).sort();
};
